<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { useField, useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n';

import type { Locales, PageDto } from '@/services/types/api.types';
import { useLanguageValidationErrors } from '@/composables';
import { provideEditLocale } from '@/composables/data-display-utils';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { toSlug } from '@/helpers/text.helper';
import { languageEnabledArray, localizableString, slug } from '@/helpers/validation.helper';
import { useCommunityStore, usePagesStore } from '@/stores';

import LanguageSelector from '@/components/LanguageSelector.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import StandardInput from '@/components/StandardInput.vue';

const { t } = useI18n();
const pagesStore = usePagesStore();
const { community } = storeToRefs(useCommunityStore());

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
}>();

const languageEnabled = ref<Locales[]>(props.languageEnabled);

// DIALOG SET-UP
defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// FORM
const { handleSubmit, errors, meta, setFieldValue, values } = useForm({
  initialValues: {
    title: Object.fromEntries(props.languageEnabled.map((locale) => [locale, ''])),
    slug: '',
    language_enabled: props.languageEnabled,
  },
  validationSchema: computed(() =>
    yup.object({
      title: localizableString(languageEnabled.value),
      slug: slug(),
      language_enabled: languageEnabledArray(community.value?.language_enabled),
    }),
  ),
});

const languageErrors = useLanguageValidationErrors(errors);

const { value: languageEnabledFieldValue } = useField<Locales[]>('language_enabled');
const editLocale = provideEditLocale(languageEnabledFieldValue);
// The title has one entry per language of the page, kept as typed when a language stays.
watch(languageEnabledFieldValue, (locales) => {
  if (!locales) return;
  setFieldValue('title', Object.fromEntries(locales.map((locale) => [locale, values.title?.[locale] ?? ''])));
  languageEnabled.value = locales;
});

// The slug follows the title, from the page's first language that has one, as a project's does, until the
// administrator types a slug of their own. Emptying the field gives it back to the title.
const slugTyped = ref(false);
watch(
  () => values.title,
  (title) => {
    if (slugTyped.value) return;
    const source = languageEnabled.value.map((locale) => title?.[locale]).find(Boolean) ?? '';
    setFieldValue('slug', toSlug(source));
  },
  { deep: true },
);

// The page starts as a draft. The list opens it once the dialog hands it back.
const onSubmit = handleSubmit(async (values) => {
  let created: PageDto | undefined;
  await wrapSavingOperation(
    async () => {
      created = await pagesStore.create(values as Pick<PageDto, 'title' | 'slug' | 'language_enabled'>);
      return true;
    },
    () => onDialogOK(created),
  );
});
</script>
<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ t('page.create.title') }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :languageEnabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <!-- LanguageSelector draws no label of its own, and right under the edit switcher its toggles need one. -->
        <div class="text-caption text-grey-7">{{ t('page.language_enabled') }}</div>
        <language-selector
          name="language_enabled"
          :label="t('page.language_enabled')"
          :language-enabled="community?.language_enabled"
        />
        <localized-input
          name="title"
          :label="t('page.title')"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
        <standard-input
          name="slug"
          :label="t('page.slug')"
          outlined
          @update:model-value="(typed: string | null) => (slugTyped = !!typed)"
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" :disabled="!meta.valid" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
